import React, { useState, useEffect, memo } from 'react';

import { useApp } from 'App';
import { ActionTypes } from 'App/types';
import { ResponseError } from 'utils/api';
import { AttachmentResponse } from 'utils/api/inbox';
import { getMimeCategory } from 'utils/mimeType';
import { notificationTypes } from 'utils/constants';
import usePrefix from 'utils/usePrefix';

import Icon from 'components/Icons';
import Button from 'components/Button';

import {
  Container,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  TabBar,
  TabItem,
  PreviewArea,
  PreviewImage,
  PreviewIframe,
  PreviewVideo,
  PreviewText,
  PreviewUnavailable,
  ModalFooter,
  FooterCounter,
  LoaderWrapper,
} from './styles';
import LoaderBouncing from '../LoaderBouncing';

interface Props {
  attachments: AttachmentResponse[];
  initialAttachment: AttachmentResponse;
  noticeId?: number;
  onClose: () => void;
  onDownload: (att: AttachmentResponse) => void;
  downloadAttachment: (noticeId: number, attachmentId: number) => Promise<Blob>;
}

const AttachmentsPreviewModal: React.FC<Props> = ({
  attachments,
  initialAttachment,
  noticeId,
  onClose,
  onDownload,
  downloadAttachment,
}) => {
  const t = usePrefix('Notices');
  const [, dispatch] = useApp();

  const [active, setActive] = useState<AttachmentResponse>(initialAttachment);
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const activeIndex = attachments.findIndex((a) => a.uri === active.uri);
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < attachments.length - 1;

  const goToPrev = () => setActive(attachments[activeIndex - 1]);
  const goToNext = () => setActive(attachments[activeIndex + 1]);

  const category = getMimeCategory(active.mimeType);

  useEffect(() => {
    if (!noticeId || !active.id) {
      setFileBlob(null);
      setFileUrl(null);
      setFetchError(true);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setFetchError(false);
    setFileBlob(null);
    setFileUrl(null);
    setIsLoading(true);
    downloadAttachment(noticeId, active.id)
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setFileBlob(blob);
        setFileUrl(createdUrl);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        const typedError = error as ResponseError;
        dispatch({
          type: ActionTypes.SET_NOTIFICATION_CODE,
          payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
        });
        setFetchError(true);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [active.id, noticeId, downloadAttachment, dispatch]);

  useEffect(() => {
    if (category !== 'text' || !fileBlob) {
      setTextContent(null);
      return;
    }
    let cancelled = false;
    setTextContent(null);
    fileBlob
      .text()
      .then((text) => {
        if (!cancelled) setTextContent(text);
      })
      .catch(() => {
        if (cancelled) return;
        dispatch({
          type: ActionTypes.SET_NOTIFICATION_CODE,
          payload: { type: notificationTypes.error },
        });
        setFetchError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fileBlob, category, dispatch]);

  const onLoaded = () => setIsLoading(false);

  const unavailable = (
    <PreviewUnavailable>
      <Icon type="attach" width="48" height="48" color="#aaa" />
      <p>{t('preview_unavailable')}</p>
      <Button label={t('cta_download')} onClick={() => onDownload(active)} />
    </PreviewUnavailable>
  );

  const renderPreview = () => {
    if (fetchError || !fileUrl) return unavailable;
    switch (category) {
      case 'image':
        return (
          <PreviewImage
            src={fileUrl}
            alt={active.name ?? ''}
            onLoad={onLoaded}
            onError={onLoaded}
            style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.2s' }}
          />
        );

      case 'pdf':
        return (
          <PreviewIframe
            src={fileUrl}
            title={active.name ?? 'PDF'}
            sandbox="allow-same-origin"
            onLoad={onLoaded}
            style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.2s' }}
          />
        );

      case 'video':
        return (
          <PreviewVideo
            controls
            key={fileUrl}
            onLoadedMetadata={onLoaded}
            onError={onLoaded}
            style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.2s' }}
          >
            <source src={fileUrl} type={active.mimeType ?? undefined} />
          </PreviewVideo>
        );

      case 'audio':
        return (
          <audio
            controls
            key={fileUrl}
            style={{ width: '100%', opacity: isLoading ? 0 : 1, transition: 'opacity 0.2s' }}
            onLoadedMetadata={onLoaded}
            onError={onLoaded}
          >
            <source src={fileUrl} type={active.mimeType ?? undefined} />
          </audio>
        );

      case 'text':
        return <PreviewText>{textContent}</PreviewText>;

      case 'document':
      case 'archive':
      case 'unknown':
      default:
        return unavailable;
    }
  };

  return (
    <Container>
      <ModalContainer>
        <ModalHeader justifyContent="space-between" alignItems="center">
          <ModalTitle>{active.name ?? active.uri}</ModalTitle>
          <Button color="link" label={t('cta_close')} onClick={onClose} />
        </ModalHeader>

        <TabBar>
          {attachments.map((att) => (
            <TabItem
              type="button"
              key={att.uri}
              active={att.uri === active.uri}
              onClick={() => setActive(att)}
            >
              {att.name ?? att.uri}
            </TabItem>
          ))}
        </TabBar>

        <PreviewArea>
          {isLoading ? (
            <LoaderWrapper>
              <LoaderBouncing />
            </LoaderWrapper>
          ) : (
            renderPreview()
          )}
        </PreviewArea>

        <ModalFooter>
          <Button
            type="button"
            color="link"
            label={t('cta_download')}
            onClick={() => onDownload(active)}
          />
          <Button
            type="button"
            color="secondary"
            label={t('cta_prev')}
            disabled={!hasPrev}
            onClick={goToPrev}
          />
          <FooterCounter>
            {activeIndex + 1} / {attachments.length}
          </FooterCounter>
          <Button
            type="button"
            color="secondary"
            label={t('cta_next')}
            disabled={!hasNext}
            onClick={goToNext}
          />
        </ModalFooter>
      </ModalContainer>
    </Container>
  );
};

export default memo(AttachmentsPreviewModal);
